import { useQuery } from '@apollo/client';
import { UserAccountLevel, UserMetaDataDocument, UserMetaDataQuery } from 'wb-reddit/graphql/generated';
import { userHasOneOfCredentials } from 'wb-reddit/app/routes/utils';
import { AppEnum, isReddit, isSpotify } from '@aleph/shared-components';
import {
	AuthGraphUserMetaDataDocument,
	AuthGraphUserMetaDataQuery,
	UserAccountLevel as AuthGraphUserAccountLevel,
} from 'wb-reddit/graphql/authgraph/generated';

interface BaseUserMetaData {
	isAdmin: boolean;
	readOnly: boolean;
	refetch: () => void;
}

// Define type with common base
export type SpotifyUserMetaData = AuthGraphUserMetaDataQuery['me'] & BaseUserMetaData;
export type DefaultUserMetaData = UserMetaDataQuery['me'] & BaseUserMetaData;

// Function overloads for better type checking
function useUserMetaData(): DefaultUserMetaData | undefined; // No param = Reddit as default
function useUserMetaData(app?: AppEnum.SPOTIFY): SpotifyUserMetaData | undefined;
function useUserMetaData(app?: AppEnum.REDDIT): DefaultUserMetaData | undefined;
function useUserMetaData(app?: AppEnum): SpotifyUserMetaData | DefaultUserMetaData | undefined;
// Implementation
function useUserMetaData(): SpotifyUserMetaData | DefaultUserMetaData | undefined {
	// Determine the app type either from parameter or from environment
	const { data, refetch } = useQuery(isReddit()
		? UserMetaDataDocument : AuthGraphUserMetaDataDocument);

	const credentials = isReddit()
		? [{ userCredentialWrite: true, credentialName: 'modify_entity' }]
		: [{ credentialName: 'modify_entity_read' }];

	// Early return if no data
	if (!data) return undefined;

	if (isSpotify()) {
		const user = data as unknown as AuthGraphUserMetaDataQuery;

		if (!user.me) return undefined;

		return {
			...user.me,
			isAdmin:  [AuthGraphUserAccountLevel.PlatformAdmin].includes(user.me.userAccountLevel),
			readOnly: !userHasOneOfCredentials(
				// @ts-ignore: its fine, dont worry about it
				user.me,
				credentials,
			),
			refetch,
		};
	}

	const user = data as unknown as UserMetaDataQuery;

	if (!user.me) return undefined;

	return {
		...user.me,
		isAdmin: [
			UserAccountLevel.Admin,
			UserAccountLevel.Agency,
			UserAccountLevel.Advertiser,
			AuthGraphUserAccountLevel.PlatformAdmin,
		].includes(user.me.userAccountLevel),
		readOnly: !userHasOneOfCredentials(
			user.me,
			credentials,
		),
		refetch,
	};
}

export default useUserMetaData;
